import type { SerpProduct, SerpSort } from '../model/types';

type SortOrder = 'asc' | 'desc';

interface SortKey {
  getValue: (product: SerpProduct) => number | null;
  order: SortOrder;
}

function getPrice({ cheapProduct }: SerpProduct): number | null {
  return cheapProduct?.price ?? null;
}

function getRating({ hotel }: SerpProduct): number | null {
  return hotel.rating;
}

function getSortRate({ cheapProduct }: SerpProduct): number | null {
  return cheapProduct?.sortRate ?? null;
}

function ascending(getValue: SortKey['getValue']): SortKey {
  return { getValue, order: 'asc' };
}

function descending(getValue: SortKey['getValue']): SortKey {
  return { getValue, order: 'desc' };
}

const SORT_KEYS: Record<SerpSort, readonly SortKey[]> = {
  popular: [descending(getSortRate), ascending(getPrice), descending(getRating)],
  priceAsc: [ascending(getPrice), descending(getRating)],
  priceDesc: [descending(getPrice), descending(getRating)],
  rating: [descending(getRating), ascending(getPrice)],
};

function toFiniteOrNull(value: number | null): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function compareValues(leftValue: number | null, rightValue: number | null, order: SortOrder): number {
  const left = toFiniteOrNull(leftValue);
  const right = toFiniteOrNull(rightValue);

  if (left === null || right === null) return Number(left === null) - Number(right === null);

  return order === 'asc' ? left - right : right - left;
}

function compareProducts(left: SerpProduct, right: SerpProduct, keys: readonly SortKey[]): number {
  for (const { getValue, order } of keys) {
    const result = compareValues(getValue(left), getValue(right), order);

    if (result !== 0) return result;
  }

  return 0;
}

export function sortSerpProducts(products: SerpProduct[], sort: SerpSort): SerpProduct[] {
  const keys = SORT_KEYS[sort];

  return [...products].sort((left, right) => compareProducts(left, right, keys));
}
